/**
 * handleGeoLevelChange.js
 * ---------------------------------------------------------------------
 * Handles transitions between geographic levels (e.g. region → constituency → ward)
 * and triggers all associated updates: data fetches, geometry reload, zoom, etc.
 *
 * Two shapes of level exist (see utils/geoLevels.js):
 *   • constituency and local_authority are TOP levels — the whole country at
 *     once, no parent area, and the area filter sent to the API is empty.
 *   • ward is a drill-down: it only ever renders the wards of one constituency,
 *     and its API requests carry that constituency's code.
 * Switching between two top levels is therefore a reset, not a drill: a
 * constituency code left in state would be sent as a local-authority area
 * filter and match nothing.
 *
 * ---------------------------------------------------------------------
 * 🧭 OVERVIEW
 *
 * 1️⃣ Detects level change and validates new level.
 * 2️⃣ If drilling down from constituency → ward:
 *     - Saves constituency bounds for later zoom use.
 * 3️⃣ Updates component state:
 *     - Clears existing styledGeo (will be rebuilt).
 *     - Keeps or updates selected feature / constituency info depending on direction.
 * 4️⃣ After state update:
 *     - Fetches metric data (primary and secondary).
 *     - Loads corresponding GeoJSON via loadGeo().
 *     - Zooms map appropriately when switching between ward/constituency.
 *     - Refreshes sidebar data and notifies onFeatureSelect().
 *
 * ---------------------------------------------------------------------
 * @param {Object} params
 * @param {string} params.newLevel - Target geo level to switch to.
 * @param {Object} params.state - Current component state.
 * @param {Object} params.mapRef - React ref to Mapbox map.
 * @param {Function} params.setState - setState function from component.
 * @param {Function} params.fitBoundsFromFeatures - Function to compute bounds from features.
 * @param {Function} params.getSelectedConstituencyName - Returns constituency name.
 * @param {Function} params.fetchMetrics - Primary metric fetcher.
 * @param {Function} [params.fetchSecondaryMetrics] - Optional secondary metric fetcher.
 * @param {Function} params.loadGeo - Function to load new geographic data.
 * @param {Function} params.getAggregatesForSidebar - Recomputes sidebar summary.
 * @param {Function} [params.onFeatureSelect] - Callback for sidebar updates.
 */

import { LEVEL_CONSTITUENCY, LEVEL_WARD, isTopLevel } from "../geoLevels";

export function handleGeoLevelChange({
  newLevel,
  state,
  mapRef,
  setState,
  fitBoundsFromFeatures,
  fetchMetrics,
  fetchSecondaryMetrics,
  loadGeo,
  getAggregatesForSidebar,
  onFeatureSelect,
}) {
  console.log("[handleGeoLevelChange] Geo level change triggered:", newLevel);
  if (!newLevel) return;

  const prevLevel = state.geoLevel;
  const prevGeoData = state.geoData;
  const prevConstituencyCode = state.selectedConstituencyCode;
  const map = mapRef?.current?.getMap?.();

  // Arriving at a top level from anywhere else drops the whole selection and
  // shows the new level nationally. A constituency code left in state would
  // otherwise be sent as the area filter for a level it does not index —
  // ward → local authority would ask for the LAs whose code is a PCON code,
  // and come back empty.
  //
  // Re-clicking the level you are already on is excluded, so the pill stays a
  // no-op rather than silently clearing the selection.
  const resetToNational = isTopLevel(newLevel) && prevLevel !== newLevel;
  // Drilling back out of wards keeps the viewport: you were looking at one
  // area and want to stay where you were, just a level up. Switching between
  // the two top levels refits the country instead, since nothing on screen
  // stays the same.
  const keepViewport = prevLevel === LEVEL_WARD;

  // ------------------------------------------------------------
  // 1️⃣ If moving constituency → ward, save constituency bounds
  // ------------------------------------------------------------
  if (
    prevLevel === LEVEL_CONSTITUENCY &&
    newLevel === LEVEL_WARD &&
    prevGeoData &&
    prevConstituencyCode
  ) {
    const consFeature = prevGeoData.features?.find(
      (f) => f.properties?.PCON24CD === prevConstituencyCode
    );
    if (consFeature) {
      const bounds = fitBoundsFromFeatures([consFeature]);
      setState({ selectedConstituencyBounds: bounds });
    }
  }

  // ------------------------------------------------------------
  // 2️⃣ Compute next state for geo-level transition
  // ------------------------------------------------------------
  setState(
    (prev) => {
      let nextFeature = prev.selectedFeature;
      let nextConstituency = prev.selectedConstituencyCode;
      let nextBounds = prev.selectedConstituencyBounds;

      // 🡩 Back out to a top level: full reset — show the whole country
      if (resetToNational) {
        nextFeature = null;
        nextConstituency = null;
        nextBounds = null;
        return {
          geoLevel: newLevel,
          styledGeo: null,
          selectedConstituencyCode: null,
          selectedConstituencyName: null,
          selectedFeature: null,
          selectedConstituencyBounds: null,
          selectedParentLevel: null,
        };
      }

      // 🡫 Constituency → Ward
      else if (prevLevel === LEVEL_CONSTITUENCY && newLevel === LEVEL_WARD) {
        if (!prev.selectedConstituencyCode && prev.selectedFeature?.value) {
          nextConstituency = prev.selectedFeature.value;
        }
      }

      return {
        geoLevel: newLevel,
        styledGeo: null,
        selectedConstituencyCode: nextConstituency,
        selectedFeature: nextFeature,
        selectedConstituencyBounds: nextBounds,
      };
    },

    // ------------------------------------------------------------
    // 3️⃣ After state update: fetch data & adjust map
    // ------------------------------------------------------------
    () => {
      const { selectedConstituencyCode, selectedConstituencyBounds, selectedFeature } =
        state;
      const consCode = selectedConstituencyCode;
      const consBounds = selectedConstituencyBounds;

      // Helper to call both metric functions safely
      const callAllMetrics = (level, codeOrNull) => {
        fetchMetrics(level, codeOrNull);
        if (typeof fetchSecondaryMetrics === "function") {
          fetchSecondaryMetrics(level, codeOrNull);
        }
      };

      if (resetToNational) {
        callAllMetrics(newLevel, null);
        loadGeo(newLevel, null, { doFit: !keepViewport });
        // Clear the selected constituency in MainPage so the sidebar resets to national
        onFeatureSelect?.({ feature: null, data: null, constCode: null, constName: null });
        return;
      }

      // Fetch new data & load geometry
      callAllMetrics(newLevel, consCode || null);
      loadGeo(newLevel, consCode || null, { doFit: false });

      // ------------------------------------------------------------
      // 4️⃣ Adjust map zoom when moving between ward/constituency
      // ------------------------------------------------------------
      if (consBounds && newLevel === LEVEL_WARD) {
        map?.fitBounds(consBounds, { padding: 28, duration: 500 });
      }

      // ------------------------------------------------------------
      // 5️⃣ Refresh sidebar & log
      // ------------------------------------------------------------
      // const data = getAggregatesForSidebar?.();
      // onFeatureSelect?.({
      //   feature: selectedFeature,
      //   data,
      // });

      console.log(`[MapPage] ✅ Switched to ${newLevel}`, {
        selectedConstituencyCode: consCode,
        selectedFeature,
      });
    }
  );
}
